import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ProjectUserTagV1Model } from '../../../../typings/api/skymap/rest/v0/.common';
import { UserByRequestResponse } from '../../../../typings/api/skymap/rest/v0/user';
import { FeatureFlagsManager } from '../../utils/core/feature-flags-manager';
import state from '../../viewer/state';
import { Button } from '../components/button/button';
import { DroneLoader } from '../components/drone-loader/drone-loader';
import { getSharedFolderId, isSharedViewerUrl } from '../routing/utils';
import { Center } from '../styles/center';
import { reset } from '../utils/styled-reset';
import { useAuthState } from './auth-state';
import { registerUnsavedChangesBridge } from './unsaved-changes-bridge';
import { getNavigationItemsFromUser, type NavigationItem } from './user-state/navigation-from-user';

export type CurrentUser = UserByRequestResponse['data'] & {
  userRoles: UserRole[];
  selectedRole: UserRole;
  tags: ProjectUserTagV1Model[];
  isAdmin: boolean;
};

export interface AppContext {
  currentUser?: CurrentUser;
  translateRole: (role: UserRole) => string;
  isAdminForCompany: (companyId: string | null | undefined) => boolean;
  hasAdminRights: boolean;
  isMobileApplication: boolean;
  isOrganizationAdmin: boolean;
  /** True when the user has work in progress that would be lost on route change. */
  pageHasUnsavedChanges: boolean;
  setPageHasUnsavedChanges: (value: boolean) => void;
}

const defaultAppContext: AppContext = {
  currentUser: undefined,
  translateRole: () => '',
  isAdminForCompany: () => false,
  hasAdminRights: false,
  isMobileApplication: false,
  isOrganizationAdmin: false,
  pageHasUnsavedChanges: false,
  setPageHasUnsavedChanges: () => undefined,
};

export const AppContext = React.createContext<AppContext>(defaultAppContext);

export const useAppContext = () => React.useContext(AppContext);

export enum UserRole {
  ProjectUser = 'dashboard',
  CompanyAdmin = 'companyAdmin',
  OrganizationAdmin = 'organizationAdmin',
}

enum Status {
  Loading,
  Error,
  Loaded,
}

type Props = {
  children: React.ReactNode;
  displayLoading?: boolean;
};

/**
 * Derives the user's role from their navigation items, using the highest available privilege.
 * OrgAdmin > CompanyAdmin > ProjectUser.
 */
const deriveSelectedRole = (navigationItems: NavigationItem[]): UserRole => {
  if (navigationItems.some((item) => item.id === 'organizationAdmin')) {
    return UserRole.OrganizationAdmin;
  }
  if (navigationItems.some((item) => item.id === 'companyAdmin')) {
    return UserRole.CompanyAdmin;
  }
  return UserRole.ProjectUser;
};

const AppState = (props: Props) => {
  const { status: authStatus, user: authUser, refreshUser } = useAuthState();
  const [fetchedUserData, setFetchedUserData] = React.useState<UserByRequestResponse['data']>();
  const [currentUser, setCurrentUser] = React.useState<CurrentUser | undefined>();
  const [status, setStatus] = React.useState<Status>(Status.Loading);
  const [pageHasUnsavedChanges, setPageHasUnsavedChanges] = React.useState(false);

  const { t } = useTranslation();

  React.useEffect(() => {
    return registerUnsavedChangesBridge(setPageHasUnsavedChanges);
  }, []);

  React.useEffect(() => {
    if (getSharedFolderId() || isSharedViewerUrl()) {
      // TODO: remove artificial delay (drone loader review)
      setTimeout(() => {
        setStatus(Status.Loaded);
        setFetchedUserData(undefined);
      }, 500);
      return;
    }

    if (authStatus === 'idle' || authStatus === 'loading') {
      setStatus(Status.Loading);
      return;
    }

    if (authStatus === 'authenticated' && authUser) {
      // TODO: remove artificial delay (drone loader review)
      setTimeout(() => {
        setFetchedUserData(authUser);
        setStatus(Status.Loaded);
      }, 500);
      return;
    }

    if (authStatus === 'unauthenticated') {
      // TODO: remove artificial delay (drone loader review)
      setTimeout(() => {
        setFetchedUserData(undefined);
        setStatus(Status.Loaded);
      }, 500);
      return;
    }

    setStatus(Status.Error);
  }, [authStatus, authUser]);

  const mapRole = React.useCallback((userRole: string) => {
    switch (userRole) {
      case 'dashboard':
        return UserRole.ProjectUser;
      case 'companyAdmin':
        return UserRole.CompanyAdmin;
      case 'organizationAdmin':
        return UserRole.OrganizationAdmin;
      default:
        return undefined;
    }
  }, []);

  const navigationItems = React.useMemo(
    () => getNavigationItemsFromUser(fetchedUserData),
    [fetchedUserData],
  );

  const isMobileApplication = useMemo<boolean>((): boolean => {
    return Boolean(state.getValue('isNativeApp'));
  }, []);

  React.useEffect(() => {
    if (!fetchedUserData || navigationItems.length === 0) {
      setCurrentUser(undefined);
      return;
    }

    const selectedRole = deriveSelectedRole(navigationItems);
    const userRoles = navigationItems
      .map((x: NavigationItem) => mapRole(x.id))
      .filter((role): role is UserRole => Boolean(role));
    const isAdmin = [UserRole.OrganizationAdmin, UserRole.CompanyAdmin].includes(selectedRole);

    setCurrentUser({
      ...fetchedUserData,
      userRoles,
      selectedRole,
      isAdmin,
      tags: fetchedUserData.projectUserTags,
    });
  }, [fetchedUserData, mapRole, navigationItems]);

  const isOrganizationAdmin = useMemo<boolean>(() => {
    return currentUser?.isOrganizationAdmin ?? false;
  }, [currentUser]);

  const hasAdminRights = useMemo<boolean>(() => {
    return currentUser?.isAdmin ?? false;
  }, [currentUser]);

  const translateRole = useCallback<AppContext['translateRole']>(
    (role) => {
      switch (role) {
        case UserRole.OrganizationAdmin:
          return t('roles.organizationAdmin', { ns: 'navigation' });
        case UserRole.CompanyAdmin:
          return t('roles.companyAdmin', { ns: 'navigation' });
        case UserRole.ProjectUser:
          return t('roles.projectUser', { ns: 'navigation' });
        default:
          return role;
      }
    },
    [t],
  );

  const isAdminForCompany = useCallback<AppContext['isAdminForCompany']>(
    (companyId) => {
      if (!currentUser || !companyId) {
        return false;
      }
      if (currentUser.isOrganizationAdmin) {
        return true;
      }
      return currentUser.companyusers.includes(companyId);
    },
    [currentUser],
  );

  const contextValues = useMemo<AppContext>(() => {
    return {
      currentUser,
      translateRole,
      isAdminForCompany,
      hasAdminRights,
      isMobileApplication,
      isOrganizationAdmin,
      pageHasUnsavedChanges,
      setPageHasUnsavedChanges,
    };
  }, [
    currentUser,
    translateRole,
    isAdminForCompany,
    hasAdminRights,
    isMobileApplication,
    isOrganizationAdmin,
    pageHasUnsavedChanges,
    setPageHasUnsavedChanges,
  ]);

  if (status === Status.Error) {
    return (
      <Center>
        <Error>
          <>
            <h3>{t('unexpectedError', { ns: 'common' })}</h3>
            <span>{t('couldNotFetchUserInformation', { ns: 'common' })}</span>
          </>

          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              void refreshUser();
            }}
          >
            {t('retry', { ns: 'common' })}
          </Button>
        </Error>
      </Center>
    );
  }

  if (status === Status.Loading) {
    if (!(props.displayLoading ?? true)) {
      return null;
    }
    return FeatureFlagsManager.instance.isEnabled('SK-6974-drone-loader') ? (
      <DroneLoader
        message={t('droneLoader.verifyingSession', { ns: 'components' })}
        mode="fullscreen"
      />
    ) : (
      <Center>{t('oneMoment', { ns: 'common' })}</Center>
    );
  }

  return <AppContext.Provider value={contextValues}>{props.children}</AppContext.Provider>;
};

const Error = styled.div`
  ${reset}
  font-size: 14px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5em;
`;

export { AppState };
